import {
  bodyChangesResponseSchema,
  type CreateHandover,
  estateLookupResultSchema,
  type HandoverBody,
  type HandoverDetail,
  type HandoverPhoto,
  handoverAgentsResponseSchema,
  handoverContactsSchema,
  handoverDetailSchema,
  handoverListSchema,
  handoverPhotoSchema,
  handoverSummarySchema,
  previousValuesSchema,
  type StartAmendment,
  type UpdateAmendment,
} from '@acasa/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { API_PREFIX, apiFetch } from '@/lib/api'

export function useHandovers() {
  return useQuery({
    queryKey: ['handovers'],
    queryFn: () => apiFetch('/handovers', handoverListSchema),
  })
}

export function useHandover(id: string | undefined) {
  return useQuery({
    queryKey: ['handover', id],
    queryFn: () => apiFetch(`/handovers/${id}`, handoverDetailSchema),
    enabled: Boolean(id),
  })
}

/** Debounced so typing an Objekt-Nr does not fire a request per keystroke. */
export function useEstateLookup(term: string) {
  const [debounced, setDebounced] = useState(term)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), 250)
    return () => clearTimeout(timer)
  }, [term])

  return useQuery({
    queryKey: ['estate-lookup', debounced],
    queryFn: () =>
      apiFetch(
        `/handovers/estates/lookup?q=${encodeURIComponent(debounced)}`,
        estateLookupResultSchema,
      ),
    enabled: debounced.trim().length >= 2,
    staleTime: 60_000,
  })
}

/**
 * The people onOffice links to this protocol's estate, offered as parties.
 *
 * Read LIVE on the server, so every fetch costs onOffice API quota — and the
 * answer does not change while somebody fills in a form. Hence fetched once per
 * editor visit and never again: no refetch on focus (a phone switching apps
 * refocuses constantly), no retries (a stairwell with no signal should get the
 * "could not reach onOffice" line, not four more attempts).
 *
 * Drafts only. A completed protocol is immutable, and the endpoint answers 409.
 * An amendment could ask, but does not need to: the people were settled when
 * the protocol was first completed, and a correction is no moment to spend
 * onOffice quota offering them again. Callers enable it for `draft` only.
 */
export function useHandoverContacts(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['handover-contacts', id],
    queryFn: () => apiFetch(`/handovers/${id}/contacts`, handoverContactsSchema),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export const handoverAgentsKey = ['handover-agents'] as const

/**
 * The Makler list from Einstellungen, for the Makler card's picker.
 *
 * Its own route rather than the settings query: that one is admin-only, and
 * every agent edits protocols. Local and cheap, so a few minutes of staleness
 * is fine — a save in Einstellungen invalidates it anyway.
 */
export function useHandoverAgents(enabled: boolean) {
  return useQuery({
    queryKey: handoverAgentsKey,
    queryFn: () => apiFetch('/handovers/agents', handoverAgentsResponseSchema),
    enabled,
    staleTime: 5 * 60_000,
  })
}

/**
 * The last completed protocol's readings and keys for the same object.
 *
 * Fetched once per editor visit: the last protocol does not change while this
 * one is being written. Drafts only — the endpoint answers 409 for a completed
 * protocol, and a completed protocol takes nothing anyway. An amendment is
 * left out too (callers enable it for `draft` only): the "last protocol" would
 * be this very one, offering its own frozen values back to it.
 */
export function usePreviousValues(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['handover-previous', id],
    queryFn: () => apiFetch(`/handovers/${id}/previous`, previousValuesSchema),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  })
}

const moveInSchema = z.object({ moveIn: handoverSummarySchema.nullable() })

/** The completed move-in for this estate, offered as a seed for a move-out. */
export function useMoveInFor(estateId: number | null) {
  return useQuery({
    queryKey: ['handover-move-in', estateId],
    queryFn: () => apiFetch(`/handovers/estates/${estateId}/move-in`, moveInSchema),
    enabled: estateId !== null,
  })
}

export function useCreateHandover() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateHandover) =>
      apiFetch('/handovers', handoverDetailSchema, { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['handovers'] }),
  })
}

export function useDeleteHandover() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_PREFIX}/handovers/${id}`, { method: 'DELETE' })
      if (!response.ok) throw new Error('Löschen fehlgeschlagen')
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['handovers'] }),
  })
}

export function useCompleteHandover(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      apiFetch(`/handovers/${id}/complete`, handoverDetailSchema, { method: 'POST' }),
    onSuccess: (detail) => {
      queryClient.setQueryData(['handover', id], detail)
      queryClient.invalidateQueries({ queryKey: ['handovers'] })
    },
  })
}

/* ----------------------------------------------------------- Fassungen --- */

/**
 * Changing a completed protocol. Each of these answers with the full detail,
 * which is written straight into the cache: the body changes underneath the
 * editor (a switch to `major` clears the signatures, a discard restores the
 * frozen body and drops the photos taken since), and a refetch would leave a
 * window in which the editor shows the old one.
 */
function useAmendmentMutation<TInput>(
  id: string,
  request: (input: TInput) => Promise<HandoverDetail>,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: request,
    onSuccess: (detail) => {
      queryClient.setQueryData(['handover', id], detail)
      queryClient.invalidateQueries({ queryKey: ['handovers'] })
      // The change list is relative to the working copy, which just changed.
      // Marked stale only — the completion dialog fetches it when it opens.
      queryClient.invalidateQueries({ queryKey: ['handover-changes', id], refetchType: 'none' })
    },
  })
}

export function useStartAmendment(id: string) {
  return useAmendmentMutation(id, (input: StartAmendment) =>
    apiFetch(`/handovers/${id}/amend`, handoverDetailSchema, { method: 'POST', body: input }),
  )
}

export function useUpdateAmendment(id: string) {
  return useAmendmentMutation(id, (input: UpdateAmendment) =>
    apiFetch(`/handovers/${id}/amend`, handoverDetailSchema, { method: 'PATCH', body: input }),
  )
}

export function useDiscardAmendment(id: string) {
  return useAmendmentMutation(id, () =>
    apiFetch(`/handovers/${id}/amend/discard`, handoverDetailSchema, { method: 'POST' }),
  )
}

/**
 * What the working copy changes against the last version.
 *
 * Fetched when the completion dialog opens, after the autosave has been
 * flushed — the server compares what it HAS, and a cached answer from a minute
 * ago would show a list that no longer matches what gets finalised. Hence no
 * stale time and no refetch on focus (the dialog is the only reader). No
 * retries: a 409 means the protocol is no longer being changed.
 */
export function useBodyChanges(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['handover-changes', id],
    queryFn: () => apiFetch(`/handovers/${id}/changes`, bodyChangesResponseSchema),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export type SaveState = 'saved' | 'saving' | 'dirty' | 'error'

/**
 * Autosave.
 *
 * The editor keeps the body in local state and pushes it after a pause, so
 * typing is never blocked by the network. Two details matter on site:
 *
 *  - The latest edit always wins. A slow save that resolves after a newer one
 *    must not overwrite the newer body, so responses from stale requests are
 *    discarded by sequence number.
 *  - A failed save is surfaced, not swallowed. Someone standing in an empty
 *    flat needs to know their meter readings did not reach the server.
 */
export function useAutosave(id: string, enabled: boolean) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<SaveState>('saved')
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)

  const pending = useRef<HandoverBody | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sequence = useRef(0)
  const inFlight = useRef(0)
  // The request under way, if any — see `flush`.
  const running = useRef<Promise<void> | null>(null)

  /**
   * Pushes whatever is pending and does not resolve until the server has it.
   *
   * With nothing pending it still WAITS for a request already on its way.
   * Callers flush before an action that replaces the body on the server —
   * finalising, switching a change to a Wesentliche Änderung (the server
   * clears the signatures), discarding. A debounced PATCH that was sent a
   * moment earlier and lands after that action would write the old body,
   * signatures included, straight back over it.
   */
  const flush = useCallback(async () => {
    const body = pending.current
    if (!body) {
      await running.current
      return
    }

    pending.current = null
    sequence.current += 1
    const ticket = sequence.current
    inFlight.current = ticket
    setState('saving')

    const run = (async () => {
      try {
        const detail = await apiFetch(`/handovers/${id}`, handoverDetailSchema, {
          method: 'PATCH',
          body: { body },
        })

        // A newer save started while this one was in flight — its result is
        // the truth, so drop this one rather than rewinding the editor.
        if (ticket !== inFlight.current) return

        queryClient.setQueryData(['handover', id], detail)
        setLastSavedAt(new Date())
        setState(pending.current ? 'dirty' : 'saved')
      } catch {
        if (ticket === inFlight.current) setState('error')
      }
    })()

    running.current = run
    await run
    if (running.current === run) running.current = null
  }, [id, queryClient])

  const save = useCallback(
    (body: HandoverBody) => {
      if (!enabled) return
      pending.current = body
      setState('dirty')

      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(flush, 800)
    },
    [enabled, flush],
  )

  // Push anything outstanding when the tab is hidden — on a phone, switching
  // apps or locking the screen is the normal way a session ends.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden' && pending.current) void flush()
    }
    document.addEventListener('visibilitychange', onHide)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      if (timer.current) clearTimeout(timer.current)
    }
  }, [flush])

  /**
   * Drops an edit that has not been sent yet. For discarding a change to a
   * completed protocol: the server restores the frozen body, and a debounced
   * PATCH firing a moment later would be refused (409) — or, worse, if the
   * timing were different, write the discarded edit back.
   */
  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    pending.current = null
    setState('saved')
  }, [])

  return { save, flush, cancel, state, lastSavedAt }
}

/**
 * Photo upload.
 *
 * Images are re-encoded in the browser before they are sent: a modern phone
 * produces 4–8 MB frames, which is slow to upload on site. Drawing through a
 * canvas also normalises EXIF orientation.
 *
 * THIS IS A TRANSPORT OPTIMISATION, NOT THE GUARANTEE. Three paths below hand
 * back the original untouched — already small, no canvas context, a file the
 * browser cannot decode — so what ends up on disk is decided by the SERVER,
 * which re-encodes every photo to one JPEG size band whatever arrives
 * (`handovers/photo-normalise.ts` in the API). `MAX_EDGE` matches its figure, so
 * the server never has to resize what came through here.
 *
 * The quality is deliberately ABOVE the server's ceiling of 85. The server's
 * pass is the one that decides the stored quality; sending it something already
 * crushed to 82 meant two lossy generations for the price of one, and the cost
 * of 0.9 is roughly 550 kB on the wire instead of 350.
 */
const MAX_EDGE = 1600
const JPEG_QUALITY = 0.9

export async function downscale(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) return file

  /*
   * The output format MUST follow the input.
   *
   * PNG is the only format here that carries alpha, and the signature pad
   * produces exactly that: dark strokes on a fully transparent ground. JPEG has
   * no alpha channel, so re-encoding composites every transparent pixel onto
   * BLACK — turning a signature into a solid black rectangle in the PDF.
   * Camera photos are JPEG and keep going down the JPEG path.
   */
  const keepsAlpha = file.type === 'image/png'

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))

    // Already small enough — send it untouched, whatever the format. A canvas
    // signature always lands here, so it never gets re-encoded at all.
    if (scale === 1 && file.size < 1_200_000) {
      bitmap.close()
      return file
    }

    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)

    const context = canvas.getContext('2d')
    if (!context) return file

    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      keepsAlpha
        ? canvas.toBlob(resolve, 'image/png')
        : canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    )
    return blob ?? file
  } catch {
    // Older browser, or a format createImageBitmap will not decode — send the
    // original rather than losing the photo.
    return file
  }
}

export function useUploadPhoto(protocolId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      file: File
      section: HandoverPhoto['section']
      refKey: string | null
    }) => {
      const blob = await downscale(input.file)
      // The extension has to match what was actually encoded — the server
      // derives the stored file's extension from the mime type.
      const name =
        blob === input.file ? input.file.name : blob.type === 'image/png' ? 'foto.png' : 'foto.jpg'

      const form = new FormData()
      form.append('file', new File([blob], name, { type: blob.type || input.file.type }))
      form.append('section', input.section)
      if (input.refKey) form.append('refKey', input.refKey)

      const response = await fetch(`${API_PREFIX}/handovers/${protocolId}/photos`, {
        method: 'POST',
        body: form,
      })

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: { message?: string }
        } | null
        throw new Error(payload?.error?.message ?? 'Foto konnte nicht gespeichert werden')
      }

      return handoverPhotoSchema.parse(await response.json())
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['handover', protocolId] }),
  })
}

export function useDeletePhoto(protocolId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (photoId: string) => {
      const response = await fetch(`${API_PREFIX}/handovers/${protocolId}/photos/${photoId}`, {
        method: 'DELETE',
      })
      // The server explains a refusal — a photo that belongs to a frozen
      // Fassung cannot be deleted — and that sentence is the useful part.
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: { message?: string }
        } | null
        throw new Error(payload?.error?.message ?? 'Foto konnte nicht gelöscht werden')
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['handover', protocolId] }),
  })
}

export function photoUrl(protocolId: string, photoId: string): string {
  return `${API_PREFIX}/handovers/${protocolId}/photos/${photoId}`
}

/** The CURRENT valid document: a draft's live preview, otherwise the last Fassung. */
export function pdfUrl(protocolId: string): string {
  return `${API_PREFIX}/handovers/${protocolId}/pdf`
}

/** The working copy, rendered live — a draft, or a change under way. */
export function previewPdfUrl(protocolId: string): string {
  return `${API_PREFIX}/handovers/${protocolId}/pdf/preview`
}

/** One frozen Fassung's own PDF, exactly as it was finalised. */
export function versionPdfUrl(protocolId: string, version: number): string {
  return `${API_PREFIX}/handovers/${protocolId}/versions/${version}/pdf`
}

/** Photos belonging to one meter, room or party. */
export function photosFor(
  detail: HandoverDetail,
  section: HandoverPhoto['section'],
  refKey: string | null,
): HandoverPhoto[] {
  return detail.photos.filter(
    (photo) => photo.section === section && (refKey === null || photo.refKey === refKey),
  )
}
